/**
 * Module: web/src/router/index.ts
 * Author: Salvo Praxis (w/ AI pair programming partner Claude Code by Anthropic)
 * Version: 1.18.0
 *
 * v1.18.0 (Engine Performance, decisions #180 + #185 / contract
 * performance-api v1.2.0 SS7): /admin/engine route (lazy), cap
 * engine_performance_view (admin-only, migration 0027), title 'Engine
 * Performance'.
 *
 * v1.17.0 (Team Performance, decision #180 / contract performance-api
 * v1.0.0): /performance route, cap performance_view, title 'Team
 * Performance'.
 *
 * v1.16.0 (the /review split -- contract entry-log-api v1.0.0 SS5, AE
 * slice 4, decisions #176 + #177): /screened-out (ScreenedOutView, gated
 * recordings_listen -- the old ReviewView cap) + /entry-log (EntryLogView,
 * gated the NEW entry_log_view cap, migration 0024). /review becomes a
 * REDIRECT to /screened-out so old deep-links never 404; ReviewView.vue is
 * deleted (decomposed into the two views + the shared EntryDetailModal).
 * Date: 2026-07-19
 *
 * v1.15.0 (decision #176 nav relabel, AE slice 3 -- titles only, paths
 * unchanged): /assisted-entry -> 'Entry Queue', /reports -> 'Reports',
 * /review -> 'Call Review' (interim until the slice-4 split), the RI
 * printable -> 'Staff Report' (the S9 all-staff relabel leftover).
 * Date: 2026-07-17
 *
 * v1.14.0 (blank-manual-entry guard -- Robert, assisted-first contract
 * v1.1.0 s1/s8.3): /dispatch gains a beforeEnter guard: a BLANK visit (no
 * ?draft= / ?phone= / ?recording= linkage) redirects queue-capable users
 * (assisted_entry_review) to /assisted-entry -- the queue is the only entry
 * path; CTS has no walk-in/radio opportunity class (Texas Rotation system).
 * Linked visits (draft promotion, /review deep-link) pass through untouched,
 * as does the hypothetical data_entry-only account (whose landing IS
 * /dispatch -- no redirect loop by construction).
 *
 * v1.13.0 (error observability, contract error-observability-api v1.0.0
 * SS8): /admin/errors -- the admin Errors view (client beacon + server
 * middleware rows), gated user_admin exactly like /admin/audit; static
 * import like its admin siblings.
 *
 * v1.1.0: /dispatch now lazy-loads the real MO entry wizard (Leaflet chunk
 * splits out of the shell bundle); its data_entry cap gate is unchanged.
 * v1.2.0: /review (decision #122 Manager Review) -- lazy-loaded, gated
 * recordings_listen (the page exposes transcripts/audio, so the manager-tier
 * listen cap is the gate; the API re-checks the manager tier server-side).
 * v1.3.0: /reports placeholder replaced by the P5 reporting area -- a
 * lazy-loaded parent (ReportsLayout) + 5 child-route tabs (standard / custom /
 * quality / data / geo). Lazy so the ECharts chunk never loads before a
 * /reports visit (the P4 Leaflet pattern); the ANY-of cap gate is unchanged
 * and inherits to every child via merged route meta.
 * v1.4.0: P6 Assisted-Entry Review Queue -- /assisted-entry becomes the real
 * queue (lazy) and gains the /assisted-entry/:id draft-detail route (lazy;
 * it carries the Leaflet map). Both keep the assisted_entry_review gate.
 * v1.5.0: P9 Review Incentives -- /review-incentives queue +
 * /review-incentives/reports (both lazy), gated review_incentives_manage
 * (contract review-incentives-api v1.0.0 sect 1/sect 7).
 * v1.6.0: /review-incentives/roster (contract v1.2.0 sect 3A, decision #143)
 * -- people/roster management, same cap, lazy.
 * v1.7.0: /help/:category?/:topic? (decision #142) -- the Help section, no
 * cap (every signed-in role), lazy (markdown-it + content split out of the
 * shell bundle); category visibility is display-scoped by cap inside the
 * view.
 * v1.8.0: P10 Call History -- /call-history (lazy; the view imports the
 * house EChart host so the echarts chunk loads only on visit), gated
 * call_history_view (contract call-history-api v1.0.0 sect 1/sect 6,
 * decision #144).
 * v1.9.0 (P8.1, finding #2234): stale-chunk recovery. Every deploy replaces
 * /var/www/portal wholesale, so a tab opened before the deploy holds lazy
 * chunk URLs that no longer exist; nginx's SPA fallback answers them with
 * index.html (200 text/html), the dynamic import() fails its MIME check,
 * and router.push() rejects SILENTLY -- left-clicks on lazy-route links
 * (e.g. every /reports tab) do nothing while right-click open-in-new-tab
 * (fresh document, fresh hashes) works. Fix: router.onError detects the
 * chunk-load failure and does ONE full-page navigation to the intended
 * route (fresh index.html is Cache-Control: no-store, so it always carries
 * current hashes); a sessionStorage one-shot per destination prevents
 * reload loops when the failure is something else (server down). A
 * vite:preloadError listener funnels dependency/CSS preload failures into
 * the same recovery.
 * v1.10.0: P12 System Config -- /admin/system-config (lazy), gated
 * system_config (contract system-config-api v1.0.0 sect 1/sect 7, decision
 * #146: admin-role-only credential manager + GoTo OAuth wizard + AI
 * procedure routing + config apply/restart).
 * v1.13.0: Module Settings -- /admin/module-settings (lazy), gated
 * module_settings (contract module-settings-api v1.0.0 sect 7: the
 * behavior-knobs sibling of System Config; SectionNav rail per module).
 * v1.11.0 (reports help pass): /reports/help -- the Reporting Guide (deep
 * layer of the two-layer reports help; the HelpTip "More →" links land on
 * its #anchors). A CHILD of /reports so it inherits the section's ANY-of
 * cap gate and renders inside ReportsLayout; lazy chunk like every tab.
 * Plus scrollBehavior: savedPosition on back/forward, hash -> smooth-scroll
 * to the anchor offset below the sticky header (vue-router computes rect
 * positions itself, so CSS scroll-margin alone doesn't apply here), and
 * DELIBERATELY no scroll reset otherwise -- filter applies are
 * router.replace query writes and must not yank the page to the top.
 * v1.12.0 (P9.3): /review-incentives/reports/:personId/print -- the
 * printable Driver Report (contract review-incentives-api v1.3.0 sect 7,
 * legacy driver-report.php parity). layout:'bare' so the document prints
 * chrome-less (no AppShell to hide); same review_incentives_manage cap;
 * lazy like the rest of RI.
 *
 * Router + the net-new global guard (doc 04 sect 9.4 -- firstline had none):
 *
 *   router.beforeEach(async (to) => {
 *     if (!auth.ready) await auth.hydrate();               // GET /whoami
 *     if (!auth.user) return { path: '/login', query:{ redirect: to.fullPath } };
 *     if (auth.mustChange && to.path !== '/change-password') return { path: '/change-password' };
 *     if (to.meta.requiresCap && !auth.hasCap(to.meta.requiresCap)) return { path: '/403' };
 *   });
 *
 * Extensions on top of the doc snippet (required for a working shell):
 *   - meta.public carve-out for /login, /pin, /reset, /reset-request
 *   - PIN sessions are confined to the dedicated limited shell /quick
 *     (doc 04 sect 9.5: "PIN sessions never hit these landings")
 *   - meta.requiresCap accepts string | string[] (ANY-of) so /reports can gate
 *     on reporting_view OR user_admin, matching the sect 9.5 landing table
 *     (an [Admin]-only user lands on /reports and must not 403 there).
 *   - '/' resolves to the sect 9.5 landing precedence via auth.landingPath.
 *
 * Nav gating stays cosmetic -- every API endpoint re-checks caps server-side.
 */
import { createRouter, createWebHistory } from 'vue-router';
import { useAuthStore } from '@/stores/auth';
import { setAuthFailureHandler } from '@/composables/useApi';

import LoginView from '@/views/LoginView.vue';
import PinLoginView from '@/views/PinLoginView.vue';
import ChangePasswordView from '@/views/ChangePasswordView.vue';
import ResetRequestView from '@/views/ResetRequestView.vue';
import ResetView from '@/views/ResetView.vue';
import ForbiddenView from '@/views/ForbiddenView.vue';
import MyEntriesView from '@/views/MyEntriesView.vue';
import QuickShellView from '@/views/QuickShellView.vue';
import AdminUsersView from '@/views/admin/AdminUsersView.vue';
import AdminPinsView from '@/views/admin/AdminPinsView.vue';
import AdminAuditView from '@/views/admin/AdminAuditView.vue';
import AdminErrorsView from '@/views/admin/AdminErrorsView.vue';

declare module 'vue-router' {
  interface RouteMeta {
    /** Reachable without a session (login / pin / reset surfaces). */
    public?: boolean;
    /** Capability gate -- string = must hold it; string[] = must hold ANY of them. */
    requiresCap?: string | string[];
    /** 'app' (default) = full shell w/ nav; 'bare' = chrome-less (login/quick). */
    layout?: 'app' | 'bare';
    /** Page title suffix. */
    title?: string;
  }
}

const router = createRouter({
  history: createWebHistory(),
  // v1.11.0: back/forward restores position; a #hash lands the anchor just
  // below the sticky header (live height -- the AppShell <header>; 61px
  // pre-mount fallback). No hash, no savedPosition -> leave scroll alone
  // (query-only replaces from filter Apply must never jump the page).
  scrollBehavior(to, _from, savedPosition) {
    if (savedPosition) return savedPosition;
    if (to.hash) {
      const header = document.querySelector('header');
      const offset = (header?.getBoundingClientRect().height ?? 61) + 12;
      return { el: to.hash, top: offset, behavior: 'smooth' };
    }
    return undefined;
  },
  routes: [
    // -- public auth surfaces (bare layout) --------------------------------
    { path: '/login', component: LoginView, meta: { public: true, layout: 'bare', title: 'Sign in' } },
    { path: '/pin', component: PinLoginView, meta: { public: true, layout: 'bare', title: 'Quick PIN access' } },
    { path: '/reset-request', component: ResetRequestView, meta: { public: true, layout: 'bare', title: 'Forgot password' } },
    { path: '/reset', component: ResetView, meta: { public: true, layout: 'bare', title: 'Reset password' } },

    // -- authenticated (full-session) surfaces ------------------------------
    { path: '/change-password', component: ChangePasswordView, meta: { title: 'Change password' } },
    { path: '/403', component: ForbiddenView, meta: { title: 'Forbidden' } },
    {
      // Manager Workspace reporting area (P5) -- lazy parent + tab children.
      // ANY-of: the Manager Workspace landing includes admin-only users (sect 9.5).
      // Children inherit requiresCap through vue-router's merged meta.
      path: '/reports',
      component: () => import('@/views/reports/ReportsLayout.vue'),
      meta: { requiresCap: ['reporting_view', 'user_admin'], title: 'Reports' },
      children: [
        { path: '', redirect: '/reports/standard' },
        { path: 'standard', component: () => import('@/views/reports/StandardReportView.vue'), meta: { title: 'Standard Report' } },
        { path: 'custom', component: () => import('@/views/reports/CustomReportView.vue'), meta: { title: 'Custom Report' } },
        { path: 'quality', component: () => import('@/views/reports/DataQualityView.vue'), meta: { title: 'Data Quality' } },
        { path: 'data', component: () => import('@/views/reports/DataView.vue'), meta: { title: 'MO Data' } },
        { path: 'geo', component: () => import('@/views/reports/GeoView.vue'), meta: { title: 'Geo Report' } },
        // Reporting Guide (v1.11.0): the deep help layer. Inherits the
        // parent's ANY-of cap gate through merged meta.
        { path: 'help', component: () => import('@/views/reports/ReportsHelpView.vue'), meta: { title: 'Reporting Guide' } },
      ],
    },
    // Lazy-loaded: the MO wizard carries Leaflet -- split it out of the shell
    // bundle so login/auth surfaces stay lean. Cap gate unchanged: data_entry.
    // v1.14.0: /dispatch is a LINKED-ENTRY surface only (assisted-first
    // contract s1) -- blank visits redirect queue-capable users to the queue.
    // Runs after the global guard, so auth is hydrated by the time it fires.
    {
      path: '/dispatch',
      component: () => import('@/views/DispatchView.vue'),
      meta: { requiresCap: 'data_entry', title: 'MO Entry' },
      beforeEnter: (to) => {
        const linked = ['draft', 'phone', 'recording'].some(
          (k) => typeof to.query[k] === 'string' && to.query[k] !== '');
        if (linked) return true;
        const auth = useAuthStore();
        if (auth.hasCap('assisted_entry_review')) return { path: '/assisted-entry' };
        return true; // data_entry-only account: the form IS their landing
      },
    },
    // AE slice 4 (contract entry-log-api SS5): the /review split.
    // Screened Out = the queue half (recordings_listen -- transcripts/audio);
    // Entry Log = the manager register (the NEW entry_log_view cap, 0024).
    { path: '/screened-out', component: () => import('@/views/ScreenedOutView.vue'), meta: { requiresCap: 'recordings_listen', title: 'Screened Out' } },
    { path: '/entry-log', component: () => import('@/views/EntryLogView.vue'), meta: { requiresCap: 'entry_log_view', title: 'Entry Log' } },
    // Old deep-links (bookmarks, the pre-split nav) land on the queue half.
    { path: '/review', redirect: '/screened-out' },
    // P6 Assisted-Entry Review Queue: system-drafted entries, mandatory human
    // review. Lazy both ways -- the detail view carries the Leaflet chunk.
    { path: '/assisted-entry', component: () => import('@/views/AssistedEntryView.vue'), meta: { requiresCap: 'assisted_entry_review', title: 'Entry Queue' } },
    { path: '/assisted-entry/:id(\\d+)', component: () => import('@/views/AssistedEntryDetailView.vue'), meta: { requiresCap: 'assisted_entry_review', title: 'Draft Review' } },
    // P10 Call History: full call review/download surface over our own
    // ingested business calls (contract call-history-api v1.0.0). Manager-only
    // cap; lazy -- carries the echarts chunk like /reports.
    { path: '/call-history', component: () => import('@/views/CallHistoryView.vue'), meta: { requiresCap: 'call_history_view', title: 'Call History' } },
    // P9 Review Incentives: the office manager credits drivers praised by name
    // in the company's own Google reviews + marks bonuses paid (contract
    // review-incentives-api v1.0.0). Manager-only cap; lazy-loaded.
    { path: '/review-incentives', component: () => import('@/views/ReviewIncentivesView.vue'), meta: { requiresCap: 'review_incentives_manage', title: 'Review Incentives' } },
    { path: '/review-incentives/reports', component: () => import('@/views/ReviewIncentiveReportsView.vue'), meta: { requiresCap: 'review_incentives_manage', title: 'Review Incentive Reports' } },
    // P9.3 printable Driver Report (contract v1.3.0 sect 7): the legacy
    // driver-report.php parity page. bare layout = chrome-less document (the
    // print CSS lives in the view); the cap gate is unaffected by layout.
    { path: '/review-incentives/reports/:personId(\\d+)/print', component: () => import('@/views/ReviewIncentivePrintView.vue'), meta: { requiresCap: 'review_incentives_manage', layout: 'bare', title: 'Staff Report' } },
    // Roster management (contract v1.2.0 sect 3A, decision #143): people are
    // identity, not credentials -- login accounts stay under /admin/users.
    { path: '/review-incentives/roster', component: () => import('@/views/ReviewRosterView.vue'), meta: { requiresCap: 'review_incentives_manage', title: 'Review Roster' } },
    // Team Performance (decision #180, contract performance-api v1.0.0):
    // per-user staff metrics -- lazy; carries the echarts chunk like /reports.
    { path: '/performance', component: () => import('@/views/PerformanceView.vue'), meta: { requiresCap: 'performance_view', title: 'Team Performance' } },
    // Driver read-only landing -- deliberately NOT cap-gated (doc 04 sect 9.4 note).
    { path: '/my-entries', component: MyEntriesView, meta: { title: 'My Entries' } },
    // Help section (decision #142): every signed-in role; lazy chunk carries
    // markdown-it + the help content, so the shell bundle stays lean.
    { path: '/help/:category?/:topic?', component: () => import('@/views/HelpView.vue'), meta: { title: 'Help' } },
    { path: '/admin/users', component: AdminUsersView, meta: { requiresCap: 'user_admin', title: 'User Admin' } },
    { path: '/admin/pins', component: AdminPinsView, meta: { requiresCap: 'user_admin', title: 'Quick PINs' } },
    { path: '/admin/audit', component: AdminAuditView, meta: { requiresCap: 'user_admin', title: 'Audit Log' } },
    // Error observability (contract error-observability-api v1.0.0 SS8):
    // client-beacon + server-middleware rows, gated exactly like the audit log.
    { path: '/admin/errors', component: AdminErrorsView, meta: { requiresCap: 'user_admin', title: 'Errors' } },
    // P12 System Config (decision #146): integrations credential manager +
    // GoTo OAuth wizard + AI procedure routing + apply/restart. Admin-only
    // cap (system_config -- PIN-excluded by construction, contract sect 1);
    // lazy -- the admin config surface never weighs on the shell bundle.
    { path: '/admin/system-config', component: () => import('@/views/admin/SystemConfigView.vue'), meta: { requiresCap: 'system_config', title: 'System Config' } },
    // Module Settings (contract module-settings-api v1.0.0): per-module
    // behavior knobs, audited + reversible via the Slice C rollback matrix.
    // Admin-only cap (module_settings -- PIN-excluded by construction); lazy.
    { path: '/admin/module-settings', component: () => import('@/views/admin/ModuleSettingsView.vue'), meta: { requiresCap: 'module_settings', title: 'Module Settings' } },
    // Engine Performance (contract performance-api v1.2.0 SS7; decisions
    // #180 + #185): judge-config registry + head-to-head comparator + rates
    // over the 0025 provenance pair. Admin-only cap (engine_performance_view
    // -- PIN-excluded by construction); lazy.
    { path: '/admin/engine', component: () => import('@/views/admin/EnginePerformanceView.vue'), meta: { requiresCap: 'engine_performance_view', title: 'Engine Performance' } },

    // -- PIN limited shell (bare -- its own chrome, no nav) -----------------
    { path: '/quick', component: QuickShellView, meta: { layout: 'bare', title: 'Quick access' } },

    // -- root + catch-all resolve through the landing precedence ------------
    { path: '/', component: ForbiddenView /* never renders -- guard redirects */, meta: { title: 'Home' } },
    { path: '/:pathMatch(.*)*', redirect: '/' },
  ],
});

/** ANY-of capability check for meta.requiresCap. */
function passesCapGate(requiresCap: string | string[] | undefined, hasCap: (c: string) => boolean): boolean {
  if (!requiresCap) return true;
  const caps = Array.isArray(requiresCap) ? requiresCap : [requiresCap];
  return caps.some((c) => hasCap(c));
}

router.beforeEach(async (to) => {
  const auth = useAuthStore();

  if (!auth.ready) await auth.hydrate(); // GET /whoami (once per boot)

  // Public routes: reachable logged-out; an already-authed session skips the
  // login/pin forms straight to its landing.
  if (to.meta.public) {
    if (auth.isAuthenticated && (to.path === '/login' || to.path === '/pin')) {
      return { path: auth.landingPath };
    }
    return true;
  }

  if (!auth.isAuthenticated) {
    return { path: '/login', query: { redirect: to.fullPath } };
  }

  // PIN sessions are confined to the dedicated limited shell (doc 04 sect 6.5/9.5).
  if (auth.sessionKind === 'pin') {
    if (to.path !== '/quick') return { path: '/quick' };
    return true;
  }
  // A full session has no business on the PIN shell -- send it to its Workspace.
  if (to.path === '/quick') {
    return { path: auth.landingPath };
  }

  // Forced-change wall: only /change-password renders until the password changes.
  if (auth.mustChange && to.path !== '/change-password') {
    return { path: '/change-password' };
  }

  // Root -> Workspace landing precedence (doc 04 sect 9.5).
  if (to.path === '/') {
    return { path: auth.landingPath };
  }

  if (!passesCapGate(to.meta.requiresCap, auth.hasCap)) {
    return { path: '/403' };
  }

  return true;
});

router.afterEach((to) => {
  document.title = to.meta.title ? `${to.meta.title} · CTS Operations Portal` : 'CTS Operations Portal';
  // Navigation landed -> any stale-chunk reload flag for this destination is
  // spent history; clear it so a FUTURE deploy can recover this route again.
  sessionStorage.removeItem(STALE_CHUNK_FLAG + to.fullPath);
});

// ---- stale-chunk recovery (v1.9.0, finding #2234) ---------------------------
// A tab from a previous deploy asks for lazy chunks the new deploy deleted;
// nginx's SPA fallback answers with index.html and the import() dies on the
// MIME check. Chrome: "Failed to fetch dynamically imported module"; Firefox:
// "error loading dynamically imported module"; Safari: "Importing a module
// script failed". One full-page navigation to the intended route fetches a
// fresh index.html (no-store) with current hashes.
const STALE_CHUNK_FLAG = 'stale-chunk-reload:';

function isStaleChunkError(err: unknown): boolean {
  const msg = err instanceof Error ? err.message : String(err);
  return /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Failed to load module script/i.test(msg);
}

/** Reload AT the target route, once per destination (loop guard). */
function recoverFromStaleChunk(targetFullPath: string): void {
  const key = STALE_CHUNK_FLAG + targetFullPath;
  if (sessionStorage.getItem(key)) return; // already tried once -- surface, don't loop
  sessionStorage.setItem(key, '1');
  window.location.assign(targetFullPath);
}

router.onError((error, to) => {
  if (isStaleChunkError(error)) {
    recoverFromStaleChunk(to?.fullPath ?? window.location.pathname + window.location.search);
  }
});

// Vite 6 fires this when a dynamic import's PRELOADED deps (CSS / shared
// chunks) fail -- same staleness class, same recovery. preventDefault stops
// vite from re-throwing after we've chosen to handle it.
window.addEventListener('vite:preloadError', (event) => {
  event.preventDefault();
  recoverFromStaleChunk(window.location.pathname + window.location.search);
});

// Central API-failure routing (registered here to keep useApi router-free):
//   dead session -> /login (preserving the intended destination)
//   forced-change 403 -> /change-password
setAuthFailureHandler((kind) => {
  const auth = useAuthStore();
  if (kind === 'unauthenticated') {
    auth.clearSession();
    const current = router.currentRoute.value;
    if (current.path !== '/login') {
      void router.push({ path: '/login', query: { redirect: current.fullPath } });
    }
  } else if (kind === 'must_change_password') {
    if (router.currentRoute.value.path !== '/change-password') {
      void router.push({ path: '/change-password' });
    }
  }
});

export default router;
